import { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ArrowLeft, Eye, EyeOff } from 'lucide-react'
import { api } from '../lib/api'
import drinxLogo from '../assets/drinx_logo_white.png'

const schema = z.object({
  password: z.string().min(12, 'Minimum 12 characters'),
  confirm: z.string(),
}).refine(d => d.password === d.confirm, { message: 'Passwords do not match', path: ['confirm'] })
type FormData = z.infer<typeof schema>

export default function ResetPassword() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token') ?? ''

  const [tokenState, setTokenState] = useState<'loading' | 'valid' | 'invalid'>('loading')
  const [done, setDone] = useState(false)
  const [showPw, setShowPw] = useState(false)
  const [apiError, setApiError] = useState<string | null>(null)

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<FormData>({
    resolver: zodResolver(schema),
  })

  useEffect(() => {
    if (!token) { setTokenState('invalid'); return }
    api.get(`/auth/validate-token?token=${token}&type=password_reset`)
      .then(() => setTokenState('valid'))
      .catch(() => setTokenState('invalid'))
  }, [token])

  async function onSubmit(data: FormData) {
    setApiError(null)
    try {
      await api.post('/auth/reset-password', { token, password: data.password })
      setDone(true)
      setTimeout(() => navigate('/login'), 2500)
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } }
      setApiError(err.response?.data?.message ?? 'Failed to reset password')
    }
  }

  return (
    <div className="min-h-screen bg-[#111111] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-1">
            <img src={drinxLogo} alt="DrinX" className="h-10 w-auto" />
          </div>
          <p className="text-gray-500 text-sm">Admin Portal</p>
        </div>

        <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl p-6">
          {tokenState === 'loading' && (
            <div className="text-center py-4 text-gray-500 text-sm">Verifying link…</div>
          )}

          {tokenState === 'invalid' && (
            <div className="text-center space-y-3">
              <p className="text-sm text-red-400">This link is invalid or has already been used.</p>
              <Link to="/forgot-password" className="block text-sm text-[#C41E3A] hover:underline">
                Request a new reset link
              </Link>
            </div>
          )}

          {tokenState === 'valid' && !done && (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <h2 className="text-base font-semibold text-white mb-1">Set new password</h2>
                <p className="text-sm text-gray-500">Choose a strong password of at least 12 characters.</p>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">New password</label>
                <div className="relative">
                  <input
                    {...register('password')}
                    type={showPw ? 'text' : 'password'}
                    autoFocus
                    className="w-full bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2.5 pr-10 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#C41E3A] transition-colors"
                    placeholder="••••••••••••"
                  />
                  <button type="button" onClick={() => setShowPw(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {errors.password && <p className="text-xs text-red-400 mt-1">{errors.password.message}</p>}
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Confirm password</label>
                <input
                  {...register('confirm')}
                  type={showPw ? 'text' : 'password'}
                  className="w-full bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#C41E3A] transition-colors"
                  placeholder="••••••••••••"
                />
                {errors.confirm && <p className="text-xs text-red-400 mt-1">{errors.confirm.message}</p>}
              </div>
              {apiError && (
                <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{apiError}</p>
              )}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#C41E3A] hover:bg-[#a51830] disabled:opacity-50 text-white text-sm font-medium py-2.5 rounded-lg transition-colors"
              >
                {isSubmitting ? 'Saving…' : 'Set password'}
              </button>
            </form>
          )}

          {done && (
            <div className="text-center space-y-3">
              <div className="w-12 h-12 rounded-full bg-green-500/15 flex items-center justify-center mx-auto">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-400">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <h2 className="text-base font-semibold text-white">Password updated</h2>
              <p className="text-sm text-gray-400">Redirecting you to sign in…</p>
            </div>
          )}
        </div>

        {tokenState !== 'loading' && !done && (
          <div className="mt-4 text-center">
            <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-300 transition-colors">
              <ArrowLeft size={14} /> Back to sign in
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}
